import { useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useDashboards, useDeleteDashboard, useTenantBackends } from '@/api/admin'
import { RequirePermission } from '@/components/RequirePermission'

interface Props {
  tenantId: string | null
}

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

// Build a URL that opens Grafana through the Keel proxy with the user's JWT.
// The token is passed as a query param (same mechanism as WebSSH).
function grafanaProxyURL(tenantId: string, path: string, orgId: number, token: string) {
  const params = new URLSearchParams({ orgId: String(orgId), token })
  return `${API_BASE}/grafana/${tenantId}${path}?${params.toString()}`
}

export function Dashboards({ tenantId }: Props) {
  const { user } = useAuth()
  const token = user?.access_token ?? ''

  const { data: dashboards, isLoading, error } = useDashboards(tenantId)
  const { data: tb } = useTenantBackends(tenantId)
  const deleteDashboard = useDeleteDashboard(tenantId ?? '')

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const grafanaOrgId = tb?.grafana_org_id ?? 0
  const grafanaReady = !!tb?.grafana_org_id

  function openNewDashboard() {
    if (!tenantId || !grafanaOrgId || !token) return
    const url = grafanaProxyURL(tenantId, '/dashboard/new', grafanaOrgId, token)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  function openDashboard(uid: string, slug: string, edit = false) {
    if (!tenantId || !grafanaOrgId || !token) return
    const basePath = `/d/${uid}/${slug}`
    const url = grafanaProxyURL(tenantId, basePath, grafanaOrgId, token)
    const finalUrl = edit ? url + '&editView=settings' : url
    window.open(finalUrl, '_blank', 'noopener,noreferrer')
  }

  function handleDelete(uid: string) {
    setConfirmDelete(uid)
  }

  function confirmDeleteAction() {
    if (!confirmDelete || !tenantId) return
    deleteDashboard.mutate(confirmDelete, {
      onSettled: () => setConfirmDelete(null),
    })
  }

  if (!tenantId) {
    return (
      <div className="page-container">
        <div style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>
          Seleziona un tenant per vedere le dashboard.
        </div>
      </div>
    )
  }

  const list = dashboards ?? []

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Dashboard Grafana</h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: '4px 0 0' }}>
            Visualizza e gestisci le dashboard metriche del tenant.
            Crea nuove dashboard nell&apos;editor Grafana integrato.
          </p>
        </div>
        {grafanaReady && token && (
          <RequirePermission perm="admin:access">
            <button className="btn btn-primary" onClick={openNewDashboard}>
              + Nuova dashboard
            </button>
          </RequirePermission>
        )}
      </div>

      {!grafanaReady && (
        <div style={{
          background: 'rgba(251,191,36,0.08)',
          border: '1px solid rgba(251,191,36,0.3)',
          borderRadius: 'var(--radius)',
          padding: '14px 18px',
          color: '#fbbf24',
          fontSize: 13,
          marginBottom: 20,
        }}>
          Grafana non ancora configurato per questo tenant.
          Attendi il completamento dell&apos;onboarding.
        </div>
      )}

      {isLoading && (
        <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Caricamento…</div>
      )}

      {error && (
        <div style={{ color: 'var(--color-error)', fontSize: 13 }}>
          Errore nel recupero delle dashboard: {String(error)}
        </div>
      )}

      {!isLoading && grafanaReady && list.length === 0 && (
        <div style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          padding: '40px 20px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Nessuna dashboard</div>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 13, marginBottom: 20 }}>
            Crea la prima dashboard per questo tenant con il pulsante &ldquo;+ Nuova dashboard&rdquo;.
          </div>
          {token && (
            <button className="btn btn-primary" onClick={openNewDashboard}>
              + Nuova dashboard
            </button>
          )}
        </div>
      )}

      {list.length > 0 && (
        <div style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                {['Titolo', 'Tags', 'Cartella', ''].map(h => (
                  <th key={h} style={{
                    textAlign: 'left',
                    padding: '10px 16px',
                    fontWeight: 500,
                    color: 'var(--color-text-muted)',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map(d => (
                <tr key={d.uid} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: 0, fontSize: 13, fontWeight: 500, textAlign: 'left' }}
                      onClick={() => openDashboard(d.uid, slugify(d.title))}
                    >
                      {d.title}
                    </button>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(d.tags ?? []).map(tag => (
                        <span key={tag} style={{
                          fontSize: 11,
                          padding: '2px 7px',
                          borderRadius: 3,
                          background: 'var(--color-surface-2)',
                          border: '1px solid var(--color-border)',
                          color: 'var(--color-text-muted)',
                          fontFamily: 'var(--font-mono)',
                        }}>{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-muted)' }}>
                    {d.folderTitle ?? '—'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12 }}
                        onClick={() => openDashboard(d.uid, slugify(d.title), false)}
                      >
                        Apri
                      </button>
                      <RequirePermission perm="admin:access">
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: 12 }}
                          onClick={() => openDashboard(d.uid, slugify(d.title), true)}
                        >
                          Modifica
                        </button>
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: 12, color: 'var(--color-error)' }}
                          onClick={() => handleDelete(d.uid)}
                        >
                          Elimina
                        </button>
                      </RequirePermission>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 28,
            width: 380,
            maxWidth: '90vw',
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Elimina dashboard</h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: '0 0 20px' }}>
              Questa azione è irreversibile. La dashboard verrà eliminata da Grafana.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>
                Annulla
              </button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--color-error)' }}
                onClick={confirmDeleteAction}
                disabled={deleteDashboard.isPending}
              >
                {deleteDashboard.isPending ? 'Eliminazione…' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
